import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Form,
  Button,
  Spin,
  Col,
  Modal,
  DatePicker,
  Row,
  Card,
  Divider,
  Typography,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { onError } from 'utils/handlers';
import Moment from 'moment';
import ModalDelete from 'components/ModalDelete';
import FormSubmitControls from 'components/FormSubmitControls';
import Table from 'components/Table';
import Select from 'components/Select';
import { ActionsNames } from 'utils/permission/index';
import { getHorarios, postHorario } from 'api/configuracion/horarios';
import { getDias } from 'api/catalogos';

const { Text } = Typography;
export const model = 'horario';
export const catalogsModels = ['catalogodedias'];

const HorariosUR = ({
  selectedRowKeys: _selectedRowKeys,
  mutable,
  API,
  permission,
}) => {
  const dias = useSelector(({ catalogs }) => catalogs.dias);
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRowKeysH, setSelectedRowKeysH] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [dataHorariosUR, setDataHorariosUR] = useState([]);
  const [dataHorarios, setDataHorarios] = useState([]);

  const fetchData = async () => {
    if (_selectedRowKeys.length) {
      const [key] = _selectedRowKeys;
      const _horariosUR = await API.getHorarios(key);
      setDataHorariosUR(_horariosUR);
      const _horarios = await getHorarios();
      setDataHorarios(_horarios.filter((e) => !_horariosUR.some((f) => f.id === e.id)));
    }
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      const [key] = _selectedRowKeys;
      const _horariosUR = key ? await API.getHorarios(key) : [];
      const _horarios = await getHorarios();
      await getDias();
      if (mounted) {
        setDataHorariosUR(_horariosUR);
        setDataHorarios(_horarios.filter((e) => !_horariosUR.some((f) => f.id === e.id)));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    form.resetFields();
    setVisible(false);
    setSelectedRowKeys([]);
    setSelectedRowKeysH([]);
  };

  const patchURS = async (idHorario) => {
    try {
      const [key] = _selectedRowKeys;
      const horarios = [...dataHorariosUR, idHorario];
      const response = await API.patch(key, { horarios });
      if (response) {
        onCancel();
        await fetchData();
      }
    } catch (err) {
      onError(err);
    }
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const response = await postHorario(values);
      if (response?.status === 201) {
        await patchURS(response.data.id);
      }

      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue({
      ...record,
      entrada: new Moment(record.entrada, 'HH:mm:ss'),
      salida: new Moment(record.salida, 'HH:mm:ss'),
    });
  };

  const handleOnRowClickHorarios = (record) => {
    setSelectedRowKeysH([record.id]);
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const rowSelectionHorarios = {
    selectedRowKeys: selectedRowKeysH,
    type: 'radio',
  };

  const requiredRule = { required: true };

  const rules = {
    required: [requiredRule],
    dias_laborales: [
      requiredRule,
      {
        validator: async (rule, val) => {
          if (val.length) {
            const values = form.getFieldsValue();
            const allValues = val.concat(values?.dias_de_descanso);
            const repetidos = [];
            let temporal = [];
            allValues.forEach((e, idx) => {
              temporal = Object.assign([], allValues);
              temporal.splice(idx, 1);
              if (temporal.indexOf(e) !== -1
                && repetidos.indexOf(e) === -1) repetidos.push(e);
            });
            if (repetidos.length) {
              throw new Error('Y');
            }
          }
        },
      },
    ],
    dias_de_descanso: [
      requiredRule,
      {
        validator: async (rule, val) => {
          if (val.length) {
            const values = form.getFieldsValue();
            const allValues = val.concat(values?.dias_laborales);
            const repetidos = [];
            let temporal = [];
            allValues.forEach((e, idx) => {
              temporal = Object.assign([], allValues);
              temporal.splice(idx, 1);
              if (temporal.indexOf(e) !== -1
                && repetidos.indexOf(e) === -1) repetidos.push(e);
            });
            if (repetidos.length) {
              throw new Error('Días');
            }
          }
        },
      },
    ],
  };

  const columns = [
    {
      titleText: 'Entrada',
      dataIndex: 'entrada',
      key: 'entrada',
      render: (val) => (new Moment(val, 'hh:mm:ss').format('LT')),
    },
    {
      titleText: 'Salida',
      dataIndex: 'salida',
      key: 'salida',
      render: (val) => (new Moment(val, 'hh:mm:ss').format('LT')),
    },
    {
      titleText: 'Días Laborales',
      dataIndex: 'dias_laborales',
      key: 'dias_laborales',
      render: (val) => {
        const days = val?.map((e) => dias.find((i) => i.id === e)?.descripcion);
        return days.map((e) => ` ${e}`).toString();
      },
    },
    {
      titleText: 'Días de Descanso',
      dataIndex: 'dias_de_descanso',
      key: 'dias_de_descanso',
      render: (val) => {
        const days = val.map((e) => dias.find((i) => i.id === e)?.descripcion);
        return days.map((e) => ` ${e}`).toString();
      },
    },
  ];

  const asignarHorario = async () => {
    setLoading(true);
    const [key] = selectedRowKeysH;
    const [keyUR] = _selectedRowKeys;
    const horarios = [...dataHorariosUR.map((e) => e.id), key];
    const saved = await API.patch(keyUR, { horarios });
    if (saved) {
      onCancel();
      await fetchData();
    }
    setLoading(false);
  };

  const onClickDelete = () => setVisibleAlert(true);

  const deleteItem = async () => {
    setLoading(true);
    const clone = [...dataHorariosUR];
    const [key] = selectedRowKeys;
    const [id] = _selectedRowKeys;
    const horarios = clone.filter((e) => e.id !== key).map((i) => i.id);
    const deleted = await API.patch(id, { horarios });
    if (deleted) {
      onCancel();
      setVisibleAlert(false);
      await fetchData();
    }
    setLoading(false);
  };

  return (
    <Row align="center" justify="center" className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Card
          className="form-container no-padding"
          bordered={false}
        >
          <Divider orientation="left"><Text>Horarios de la UR</Text></Divider>
          <Table
            cols={columns}
            data={dataHorariosUR}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickDelete,
            }}
            noControls={!mutable}
            allowSort={false}
            allowSearch={false}
            rowKey="id"
            permission={permission}
          />
          <Divider orientation="left"><Text>Horarios</Text></Divider>
          <Table
            cols={columns}
            mobileCols={[columns[1]]}
            data={dataHorarios}
            rowSelection={rowSelectionHorarios}
            handleOnRowClick={handleOnRowClickHorarios}
            customActions={[
              {
                onClick: asignarHorario,
                icon: <PlusOutlined />,
                text: 'Asignar Horario',
                disabled: !selectedRowKeysH.length,
                permission: {
                  model,
                  action: ActionsNames.add,
                },
              },
            ]}
            noControls={!mutable}
            allowSort={false}
            allowSearch={false}
            rowKey="id"
          />
          <Modal
            visible={visible}
            closable={false}
            footer={null}
            title={(
              <FormSubmitControls
                label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Horario`}
                onFinish={onFinish}
                onCancel={onCancel}
                loading={loading}
              />
            )}
            keyboard={!loading}
            maskClosable={!loading}
            onCancel={onCancel}
            forceRender
          >
            <Form
              form={form}
              layout="vertical"
              onFinish={onFinish}
            >
              <Row gutter={10}>
                <Col span={12}>
                  <Form.Item
                    name="entrada"
                    label="Hora de entrada"
                    rules={rules.required}
                    hasFeedback
                  >
                    <DatePicker
                      placeholder=""
                      suffixIcon=""
                      mode="time"
                      picker="time"
                      format="h:mm a"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="salida"
                    label="Hora de salida"
                    rules={rules.required}
                    hasFeedback
                  >
                    <DatePicker
                      suffixIcon=""
                      placeholder=""
                      mode="time"
                      picker="time"
                      format="h:mm a"
                    />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="dias_laborales"
                    label="Días Laborales"
                    rules={rules.dias_laborales}
                    hasFeedback
                  >
                    <Select mode="multiple" dataSource={dias} />
                  </Form.Item>
                </Col>
                <Col span={24}>
                  <Form.Item
                    name="dias_de_descanso"
                    rules={rules.dias_de_descanso}
                    label="Días de Descanso"
                  >
                    <Select mode="multiple" dataSource={dias} />
                  </Form.Item>
                </Col>

                <Form.Item hidden>
                  <Button htmlType="submit" />
                </Form.Item>
              </Row>
            </Form>
          </Modal>
          <ModalDelete
            onDelete={deleteItem}
            onCancel={() => setVisibleAlert(false)}
            visible={visibleAlert}
            content="el Horario de la UR"
          />
        </Card>
      </Spin>
    </Row>
  );
};

HorariosUR.propTypes = {
  API: PropTypes.shape({
    get: PropTypes.func.isRequired,
    post: PropTypes.func.isRequired,
    patch: PropTypes.func.isRequired,
    delete: PropTypes.func.isRequired,
    getHorarios: PropTypes.func.isRequired,
  }).isRequired,
  selectedRowKeys: PropTypes.arrayOf(PropTypes.number),
  mutable: PropTypes.bool.isRequired,
  permission: PropTypes.shape({
    add: PropTypes.bool.isRequired,
    authorize: PropTypes.bool.isRequired,
    cancel: PropTypes.bool.isRequired,
    change: PropTypes.bool.isRequired,
    delete: PropTypes.bool.isRequired,
    export: PropTypes.bool.isRequired,
    import: PropTypes.bool.isRequired,
    sync: PropTypes.bool.isRequired,
    view: PropTypes.bool.isRequired,
  }).isRequired,
};
HorariosUR.defaultProps = {
  selectedRowKeys: [],
};

export default HorariosUR;
